import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Calendar,
  Bed,
  BarChart3,
  ClipboardList,
  ArrowLeft
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { ADMIN_ICON_STROKE } from './admin/AdminShared';

const navItems = [
  { to: '/admin', label: 'Dashboard', end: true, icon: LayoutDashboard },
  { to: '/admin/stays', label: 'Stays', icon: Building2 },
  { to: '/admin/events', label: 'Events', icon: Calendar },
  { to: '/admin/rooms', label: 'Rooms', icon: Bed },
  { to: '/admin/inventory', label: 'Inventory', icon: BarChart3 },
  { to: '/admin/bookings', label: 'Bookings', icon: ClipboardList }
];

const navLinkClass = ({ isActive }) =>
  `whitespace-nowrap px-4 py-2 rounded-full text-[14px] font-medium transition-colors shrink-0 inline-flex items-center gap-2 ${
    isActive
      ? 'bg-black text-white'
      : 'bg-white text-black border border-[#E5E3DF] hover:bg-[#FAF7F2]'
  }`;

/**
 * Shared shell for /admin/* — matches main site background and typography.
 */
const AdminLayout = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const displayName = user?.name?.trim() || user?.phone || user?.email || 'Admin';
  const isAdminDashboard = location.pathname === '/admin' || location.pathname === '/admin/';

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col">
      <header className="bg-white border-b border-[#E5E3DF] sticky top-0 z-40 shadow-[0_1px_0_rgba(0,0,0,0.04)]">
        <div className="max-w-6xl mx-auto w-full px-5 md:px-[120px] py-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 min-w-0">
              {!isAdminDashboard && (
                <Link
                  to="/admin"
                  className="inline-flex items-center justify-center p-2 rounded-full bg-[#FAF7F2] text-[#FF5E4C] border border-[#E5E3DF] hover:bg-[#f5f0e8] shrink-0 relative z-50"
                  aria-label="Back to admin dashboard"
                >
                  <ArrowLeft className="w-5 h-5" strokeWidth={ADMIN_ICON_STROKE} aria-hidden />
                </Link>
              )}
              <Link
                to="/"
                className="text-[16px] font-medium text-black tracking-[-0.02em] hover:opacity-80 truncate"
              >
                Book For Paws
              </Link>
              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#FAF7F2] text-[#5c5c5c] border border-[#E5E3DF] shrink-0">
                Admin
              </span>
            </div>
            <div className="flex items-center gap-3 text-[13px] shrink-0">
              <span className="text-[#78736A] truncate max-w-[160px]">{displayName}</span>
              <Link to="/" className="font-medium text-[#FF5E4C] hover:opacity-90">
                View site
              </Link>
            </div>
          </div>
          <nav className="flex gap-2 overflow-x-auto pb-0.5 -mx-1 px-1 [scrollbar-width:thin]">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={navLinkClass}>
                <item.icon className="w-4 h-4 shrink-0" strokeWidth={ADMIN_ICON_STROKE} aria-hidden />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      <main className="flex-1 w-full max-w-6xl mx-auto px-5 md:px-[120px] py-6 md:py-8 pb-12">
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
